import React, { useState } from 'react';
import { Container, Row, Col, Card, Badge, Modal, Button } from 'react-bootstrap';
import SEOHead from '../components/SEOHead';

interface WorkItem {
  id: number;
  title: string;
  category: string;
  image: string;
  beforeImage?: string;
  description: string;
  services: string[];
  completionTime: string;
  customerRating: number;
}

interface OurWorkPageProps {
  onBookingClick?: () => void;
}

const OurWorkPage: React.FC<OurWorkPageProps> = ({ onBookingClick }) => {
  const [selectedWork, setSelectedWork] = useState<WorkItem | null>(null);
  const [filter, setFilter] = useState<string>('all');

  const workGallery: WorkItem[] = [
    {
      id: 1,
      title: "Maruti Swift Complete Makeover",
      category: "denting-painting",
      image: "/work/swift-after.jpg",
      beforeImage: "/work/swift-before.jpg",
      description: "Complete denting and painting work for accident-damaged Maruti Swift. Restored to showroom condition.",
      services: ["Denting & Painting", "Body Repair", "Polishing"],
      completionTime: "3 days",
      customerRating: 5
    },
    {
      id: 2,
      title: "Honda City AC Repair",
      category: "ac-service",
      image: "/work/honda-ac.jpg",
      description: "Complete AC system overhaul including compressor replacement and gas refill.",
      services: ["AC Repair", "Compressor Replacement", "Gas Refill"],
      completionTime: "1 day",
      customerRating: 5
    },
    {
      id: 3,
      title: "Hyundai i20 Engine Service",
      category: "mechanical",
      image: "/work/i20-engine.jpg",
      description: "Complete engine servicing with oil change, filter replacement, and tune-up.",
      services: ["Engine Service", "Oil Change", "Filter Replacement"],
      completionTime: "4 hours",
      customerRating: 4
    },
    {
      id: 4,
      title: "Toyota Innova Suspension Repair",
      category: "suspension",
      image: "/work/innova-suspension.jpg",
      description: "Front suspension repair with shock absorber replacement and wheel alignment.",
      services: ["Suspension Repair", "Shock Absorbers", "Wheel Alignment"],
      completionTime: "2 days",
      customerRating: 5
    },
    {
      id: 5,
      title: "Tata Nexon Paint Job",
      category: "denting-painting",
      image: "/work/nexon-paint.jpg",
      beforeImage: "/work/nexon-before.jpg",
      description: "Custom paint job with scratch removal and full body polishing.",
      services: ["Custom Painting", "Scratch Removal", "Body Polishing"],
      completionTime: "2 days",
      customerRating: 5
    },
    {
      id: 6,
      title: "Mahindra XUV Battery Service",
      category: "battery",
      image: "/work/xuv-battery.jpg",
      description: "Battery replacement and electrical system check for starting issues.",
      services: ["Battery Replacement", "Electrical Check", "Diagnostics"],
      completionTime: "2 hours",
      customerRating: 4
    }
  ];

  const categories = [
    { key: 'all', label: 'All Work', icon: '🔧' },
    { key: 'denting-painting', label: 'Denting & Painting', icon: '🎨' },
    { key: 'mechanical', label: 'Mechanical', icon: '⚙️' },
    { key: 'ac-service', label: 'AC Service', icon: '❄️' },
    { key: 'suspension', label: 'Suspension', icon: '🚗' },
    { key: 'battery', label: 'Battery', icon: '🔋' }
  ];

  const filteredWork = filter === 'all' 
    ? workGallery 
    : workGallery.filter(work => work.category === filter);

  const renderStars = (rating: number) => {
    return '⭐'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  return (
    <>
      <SEOHead page="work" />
      
      {/* Hero Section */}
      <section className="bg-gradient-primary text-white py-5">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h1 className="display-4 fw-bold mb-3">Our Work Gallery</h1>
              <p className="lead mb-0">
                See the quality of our work through real customer projects. 
                Every job reflects our commitment to excellence and customer satisfaction.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Filter Tabs */}
      <section className="py-4 bg-light">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="d-flex flex-wrap justify-content-center gap-2">
                {categories.map(category => (
                  <Button
                    key={category.key}
                    variant={filter === category.key ? 'primary' : 'outline-primary'}
                    onClick={() => setFilter(category.key)}
                    className="mb-2"
                  >
                    {category.icon} {category.label}
                  </Button>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Work Gallery */}
      <section className="py-5">
        <Container>
          <Row>
            {filteredWork.map(work => (
              <Col lg={4} md={6} className="mb-4" key={work.id}>
                <Card className="h-100 shadow-sm work-card" style={{cursor: 'pointer'}} onClick={() => setSelectedWork(work)}>
                  <div className="position-relative">
                    <Card.Img 
                      variant="top" 
                      src={work.image} 
                      style={{height: '200px', objectFit: 'cover'}}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200/064DA1/FFFFFF?text=Work+Image';
                      }}
                    />
                    <Badge bg="primary" className="position-absolute top-0 end-0 m-2">
                      {work.completionTime}
                    </Badge>
                  </div>
                  <Card.Body>
                    <h5 className="fw-bold mb-2">{work.title}</h5>
                    <p className="text-muted small mb-3">{work.description}</p>
                    
                    <div className="mb-3">
                      {work.services.map((service, index) => (
                        <Badge key={index} bg="warning" text="dark" className="me-1 mb-1">
                          {service}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">Customer Rating:</small>
                      <span>{renderStars(work.customerRating)}</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Work Detail Modal */}
      <Modal show={!!selectedWork} onHide={() => setSelectedWork(null)} size="lg">
        {selectedWork && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{selectedWork.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row>
                {selectedWork.beforeImage && (
                  <Col md={6} className="mb-3">
                    <h6>Before</h6>
                    <img 
                      src={selectedWork.beforeImage} 
                      alt="Before" 
                      className="img-fluid rounded"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200/ED1C23/FFFFFF?text=Before';
                      }}
                    />
                  </Col>
                )}
                <Col md={selectedWork.beforeImage ? 6 : 12} className="mb-3">
                  <h6>{selectedWork.beforeImage ? 'After' : 'Completed Work'}</h6>
                  <img 
                    src={selectedWork.image} 
                    alt="After" 
                    className="img-fluid rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200/064DA1/FFFFFF?text=After';
                    }}
                  />
                </Col>
              </Row>
              
              <div className="mt-3">
                <h6>Project Details:</h6>
                <p>{selectedWork.description}</p>
                
                <div className="mb-3">
                  <strong>Services Performed:</strong><br/>
                  {selectedWork.services.map((service, index) => (
                    <Badge key={index} bg="primary" className="me-1 mb-1">
                      {service}
                    </Badge>
                  ))}
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <strong>Completion Time:</strong> {selectedWork.completionTime}
                  </div>
                  <div className="col-md-6">
                    <strong>Customer Rating:</strong> {renderStars(selectedWork.customerRating)}
                  </div>
                </div>
              </div>
            </Modal.Body>
          </>
        )}
      </Modal>

      {/* CTA Section */}
      <section className="py-5 bg-yellow text-dark">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h3 className="fw-bold mb-3">Want Similar Quality Work for Your Car?</h3>
              <p className="mb-4">
                Join thousands of satisfied customers who trust us with their vehicles. 
                Get professional service with guaranteed quality.
              </p>
              <Button variant="dark" size="lg" className="fw-bold px-4" onClick={onBookingClick}>
                📞 Book Your Service Now
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default OurWorkPage;